import { Component, computed, inject, input, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwAvatar } from '../../shared/ui/avatar/uw-avatar';
import { WorkbenchStore } from '../../core/stores/workbench.store';
import { CopilotStore } from '../../core/stores/copilot.store';
import { QueueStore } from '../../core/stores/queue.store';
import { AccountStore } from '../../core/stores/account.store';
import { NAV_GROUPS } from '../../core/config/navigation.config';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'uw-sidebar',
  standalone: true,
  imports: [UwIcon, UwAvatar, RouterLink],
  template: `
    <aside
      class="flex h-full shrink-0 flex-col border-r border-line bg-surface transition-all duration-200 ease-in-out"
      [class.w-16]="collapsed()"
      [class.w-60]="!collapsed()"
    >
      <div
        class="flex h-14 shrink-0 items-center gap-2.5 border-b border-line"
        [class.justify-center]="collapsed()"
        [class.px-4]="!collapsed()"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ai text-white shadow-sm"
        (click)="workbench.toggleSidebar()">
          <uw-icon name="sparkles" [size]="16" />
        </span>
        @if (!collapsed()) {
          <div class="min-w-0 leading-tight">
            <p class="truncate text-[13.5px] font-bold text-primary">Workbench</p>
            <p class="truncate text-[9.5px] font-semibold uppercase tracking-wider text-text-muted">Underwriting Workbench</p>
          </div>
          <button
            class="ml-auto inline-flex items-center justify-center rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
            (click)="workbench.toggleSidebar()"
            [attr.aria-label]="'Collapse sidebar'"
            [title]="'Collapse sidebar'"
          >
            <uw-icon name="panel-left" [size]="14" />
          </button>
        }
      </div>

      <div class="flex-1 overflow-y-auto px-2 py-4">
        @if (workbench.mode() === 'copilot') {
          <div class="space-y-1">
            @if (!collapsed()) {
              <p class="typo-label px-2 pb-1 pt-0.5">AI Copilot</p>
            }
            <a
              class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium transition-colors hover:bg-surface-sunken hover:text-primary"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              [class.bg-primary-soft]="isCopilotTriage()"
              [class.text-primary]="isCopilotTriage()"
              [class.text-secondary]="!isCopilotTriage()"
              href="/workbench"
              [title]="'Triage queue'"
              (click)="openTriage($event)"
            >
              <uw-icon name="bot" [size]="15" />
              @if (!collapsed()) {
                <span class="flex-1">Triage queue</span>
                <span class="rounded-full bg-surface-sunken px-1.5 text-[10px] font-bold text-secondary">{{ queue.items().length }}</span>
              }
            </a>
            <a
              class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium transition-colors hover:bg-surface-sunken hover:text-primary"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              [class.bg-primary-soft]="isCopilotAccount()"
              [class.text-primary]="isCopilotAccount()"
              [class.text-secondary]="!isCopilotAccount()"
              href="#"
              [title]="'Active account'"
              (click)="openActiveAccount($event)"
            >
              <uw-icon name="building" [size]="15" />
              @if (!collapsed()) {
                <span class="flex-1 truncate">Active account</span>
              }
            </a>

            @if (!collapsed()) {
              <p class="typo-label px-2 pb-1 pt-3">Prioritization</p>
            }
            <a
              class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium text-secondary transition-colors hover:bg-surface-sunken hover:text-primary"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              href="/workbench"
              [title]="'High priority'"
              (click)="filter('high', $event)"
            >
              <uw-icon name="alert-triangle" [size]="15" class="text-warning" />
              @if (!collapsed()) {
                <span class="flex-1">High priority</span>
                <span class="rounded-full bg-surface-sunken px-1.5 text-[10px] font-bold text-secondary">{{ queue.highPriorityCount() }}</span>
              }
            </a>
            <a
              class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium text-secondary transition-colors hover:bg-surface-sunken hover:text-primary"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              href="/workbench"
              [title]="'Straight-through'"
              (click)="filter('straight-through', $event)"
            >
              <uw-icon name="check-circle" [size]="15" class="text-success" />
              @if (!collapsed()) {
                <span class="flex-1">Straight-through</span>
                <span class="rounded-full bg-surface-sunken px-1.5 text-[10px] font-bold text-secondary">{{ queue.straightThroughCount() }}</span>
              }
            </a>
            <a
              class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium text-secondary transition-colors hover:bg-surface-sunken hover:text-primary"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              href="/workbench"
              [title]="'Blocked'"
              (click)="filter('blocked', $event)"
            >
              <uw-icon name="shield-off" [size]="15" class="text-danger" />
              @if (!collapsed()) {
                <span class="flex-1">Blocked</span>
                <span class="rounded-full bg-surface-sunken px-1.5 text-[10px] font-bold text-secondary">{{ queue.blockedCount() }}</span>
              }
            </a>
          </div>
        } @else {
          <div class="space-y-4">
            @for (group of NAV_GROUPS; track group.id) {
              @if (group.gap && $index > 0) {
                <div class="my-2 border-t border-line"></div>
              }
              @if (!collapsed()) {
                <p class="typo-label px-2">{{ group.label }}</p>
              }
              <div class="space-y-1">
                @for (item of group.items; track item.id) {
                  <a
                    class="flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] font-medium transition-colors hover:bg-surface-sunken hover:text-primary"
                    [class.justify-center]="collapsed()"
                    [class.px-0]="collapsed()"
                    [class]="navItemClass(item.route)"
                    [routerLink]="item.route"
                    [title]="item.label"
                    (click)="onNavClick()"
                  >
                    <uw-icon [name]="item.icon" [size]="15" class="shrink-0" />
                    @if (!collapsed()) {
                      <span class="flex-1 truncate">{{ item.label }}</span>
                      @if (item.badge) {
                        <span class="rounded-full bg-ai-soft px-1.5 text-[10px] font-bold text-ai">{{ item.badge }}</span>
                      }
                    }
                  </a>
                }
              </div>
            }
          </div>
        }
      </div>

      <div class="shrink-0 border-t border-line p-2">
        @if (expanded()) {
          <div class="flex items-center gap-2 rounded-lg px-2 py-1.5">
            <uw-avatar [initials]="user.initials" [label]="user.name" size="sm" tone="accent" />
            <div class="min-w-0 flex-1 leading-tight">
              <p class="truncate text-[11.5px] font-semibold text-primary">{{ user.name }}</p>
              <p class="truncate text-[10px] text-text-muted">{{ user.role }}</p>
            </div>
          </div>
        } @else if (collapsed()) {
          <button
            class="flex w-full items-center justify-center rounded-lg p-2 text-secondary transition-colors hover:bg-surface-sunken"
            (click)="workbench.toggleSidebar()"
            [attr.aria-label]="'Expand sidebar'"
            [title]="'Expand sidebar'"
          >
            <uw-icon name="panel-right" [size]="16" />
          </button>
        } @else {
          <div class="flex items-center gap-2 rounded-lg px-2 py-1.5">
            <uw-avatar [initials]="user.initials" [label]="user.name" size="sm" tone="accent" />
            <div class="min-w-0 flex-1 leading-tight">
              <p class="truncate text-[11.5px] font-semibold text-primary">{{ user.name }}</p>
              <p class="truncate text-[10px] text-text-muted">{{ user.role }}</p>
            </div>
            <button
              class="inline-flex items-center justify-center rounded p-1 text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
              (click)="workbench.toggleSidebar()"
              [attr.aria-label]="'Collapse sidebar'"
              [title]="'Collapse sidebar'"
            >
              <uw-icon name="panel-left" [size]="14" />
            </button>
          </div>
        }
      </div>
    </aside>
  `,
})
export class UwSidebar {
  readonly expanded = input(false);

  protected readonly NAV_GROUPS = NAV_GROUPS;

  protected readonly workbench = inject(WorkbenchStore);
  private readonly copilot = inject(CopilotStore);
  protected readonly queue = inject(QueueStore);
  private readonly account = inject(AccountStore);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  protected get user() {
    return this.auth.currentUser();
  }

  protected readonly collapsed = computed(() => !this.expanded() && this.workbench.sidebarCollapsed());

  private readonly currentUrl = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  protected navItemClass(route: string): string {
    return this.currentUrl().startsWith(route)
      ? 'bg-primary-soft text-primary'
      : 'text-secondary hover:bg-surface-sunken hover:text-primary';
  }

  protected isCopilotTriage(): boolean {
    return this.currentUrl() === '/workbench';
  }

  protected isCopilotAccount(): boolean {
    return this.currentUrl().startsWith('/workbench/account');
  }

  protected onNavClick(): void {
    this.workbench.setMode('classic');
    this.workbench.setMobileNav(false);
  }

  protected openTriage(event: Event): void {
    event.preventDefault();
    this.copilot.setView('triage');
    this.workbench.setMode('copilot');
    this.workbench.setMobileNav(false);
    this.queue.setFilter('all');
    this.queue.setSearch('');
    void this.router.navigate(['/workbench']);
  }

  protected openActiveAccount(event: Event): void {
    event.preventDefault();
    this.copilot.setView('account');
    this.workbench.setMode('copilot');
    this.workbench.setMobileNav(false);
    void this.router.navigate(['/workbench/account', this.account.selectedAccountId()]);
  }

  protected filter(category: 'high' | 'straight-through' | 'blocked', event: Event): void {
    event.preventDefault();
    this.copilot.setView('triage');
    this.workbench.setMode('copilot');
    this.workbench.setMobileNav(false);
    this.queue.setFilter(category);
    this.queue.setSearch('');
    void this.router.navigate(['/workbench']);
  }
}
